import type React from "react";
import { cn } from "@marketing-sites/utils/cn";
import { Collapsible } from "@base-ui/react/collapsible";
import {
  navFg,
  navSubheadFg,
  navFgMutedMobile,
  floatingUnderlineBase,
  floatingUnderlineGroupHover,
  floatingUnderlineSelfHover,
} from "../floating-nav-classes";
import { ChevronDownIcon } from "../../icons/chevron-down-icon";
import type {
  MobileMenuNavItem,
  NavLink,
  NavLinkGrid,
  NavLinkSection,
} from "../../types";
import { HeaderLink } from "../header-link";
import { Text } from "../text";
import { Button } from "../button";
import { useHeaderContext } from "../../context";

const RowArrow = ({ external }: { external?: boolean }) => (
  <span
    aria-hidden
    className={cn(
      "transition-[translate,color] duration-150 ease-out",
      external
        ? "group-hover:-translate-y-[3px] group-hover:translate-x-[3px]"
        : "group-hover:translate-x-[3px]",
    )}
  >
    {external ? "↗" : "→"}
  </span>
);

// A grid of links laid out across N columns within a drawer section
const NavLinkGridItem = ({ grid }: { grid: NavLinkGrid }) => (
  <li
    className="grid gap-2"
    style={{ gridTemplateColumns: `repeat(${grid.columns}, minmax(0, 1fr))` }}
  >
    {grid.links.map((link: NavLink, index) => (
      <HeaderLink
        // eslint-disable-next-line analytics/id-must-be-static -- forwarded from the row's authored id; every nav row names itself with a literal, never copy or a translated string
        analyticsId={`${link.analyticsId}_dialog`}
        key={index}
        href={link.href}
        text={link.title}
        analyticsText={link.analyticsText}
        className="flex items-center gap-2.5 py-2"
      >
        {link.icon && (
          <div className="bg-nav-content-primary/5 rounded-sm">{link.icon}</div>
        )}
        <Text variant="btnLg" className="text-nav-content-primary">
          {link.title}
        </Text>
      </HeaderLink>
    ))}
  </li>
);

type DialogNavItemProps = {
  item: MobileMenuNavItem;
  className?: string;
  openItems?: Set<string>;
  onOpenItemChange?: React.Dispatch<React.SetStateAction<Set<string>>>;
};

// Helper: Renders a list of dropdown links
const DropdownLinks = ({ links }: { links: NavLinkSection[][] }) => (
  <div className="grid grid-cols-2 gap-6 pb-5">
    {links.map((sections) =>
      sections.map((section) => (
        <div
          key={section.title}
          className="col-span-full flex flex-col gap-1 pl-2 pt-2"
        >
          {section.title && (
            <Text
              variant="labelSm"
              className="text-nav-content-tertiary font-normal"
            >
              {section.title}
            </Text>
          )}
          <ul className="flex flex-col gap-1">
            {section.links.map((link, linkIndex) => {
              if ("columns" in link)
                return <NavLinkGridItem key={linkIndex} grid={link} />;
              // prose / fullWidthRow are floating-variant only.
              if ("prose" in link || "fullWidthRow" in link) return null;
              return (
                <li key={linkIndex}>
                  <HeaderLink
                    // eslint-disable-next-line analytics/id-must-be-static -- forwarded from the row's authored id; every nav row names itself with a literal, never copy or a translated string
                    analyticsId={`${link.analyticsId}_dialog`}
                    href={link.href}
                    text={link.title}
                    analyticsText={link.analyticsText}
                    className="flex items-center gap-2.5 py-2"
                  >
                    {link.icon && (
                      <div className="bg-nav-content-primary/5 rounded-sm">
                        {link.icon}
                      </div>
                    )}
                    {link.image && (
                      <div className="aspect-square w-10 shrink-0 overflow-hidden rounded-sm">
                        {link.image}
                      </div>
                    )}
                    <span className="block">
                      <Text
                        variant="btnLg"
                        className="text-nav-content-primary mb-1 block"
                      >
                        {link.title}
                      </Text>
                      <Text
                        variant="bodyMd"
                        className="text-nav-content-tertiary block"
                      >
                        {link.description}
                      </Text>
                    </span>
                  </HeaderLink>
                </li>
              );
            })}
          </ul>
        </div>
      )),
    )}
  </div>
);

// Floating-variant accordion body — matches the prototype spec. Takes a flat
// list of mobile sections (bespoke re-grouping supplied via `mobileLinks`, or
// the desktop columns flattened as a fallback).
// Two row treatments carried over from the desktop panels: `primary` rows are
// the prominent section leads (medium), everything else is a compact sub-row.
const floatingRowTitle = {
  primary: cn(
    "text-[1rem] font-medium leading-[1.1] tracking-[-0.01rem]",
    navFg,
  ),
  secondary: cn(
    "text-[0.875rem] leading-[0.929] md:text-[1rem] md:leading-[0.938]",
    navFg,
  ),
};
const floatingRowDescription = {
  primary: cn("mt-1 text-[0.9375rem] leading-[1.4]", navSubheadFg),
  secondary: cn(
    "mt-1 text-[0.875rem] leading-[1.286] md:text-[1rem]",
    navFgMutedMobile,
  ),
};
// Sections that carry a group label (e.g. Resources' Learn/Support/Company)
// render the label as the section title and every link beneath it uniformly
// small — no per-row primary/secondary emphasis.
const floatingLabeledTitle = cn("text-[0.875rem] leading-[0.929]", navFg);
const floatingLabeledDescription = cn(
  "mt-1 text-[0.875rem] leading-[1.4]",
  navFgMutedMobile,
);

const FloatingDropdownLinks = ({
  sections,
}: {
  sections: NavLinkSection[];
}) => (
  <div className="flex flex-col gap-6 pb-5">
    {sections.map((section, sectionIndex) => {
      const labeled = Boolean(section.title);
      return (
        <div key={sectionIndex} className="flex flex-col gap-1 pl-2 pt-2">
          {section.title && (
            <p className={floatingRowTitle.primary}>{section.title}</p>
          )}
          <ul className="flex flex-col gap-1">
            {section.links.map((link, linkIndex) => {
              if ("columns" in link)
                return (
                  <NavLinkGridItem key={linkIndex} grid={link as NavLinkGrid} />
                );
              if ("fullWidthRow" in link) {
                const fwr = link;
                return (
                  <li key={linkIndex}>
                    <HeaderLink
                      // eslint-disable-next-line analytics/id-must-be-static -- forwarded from the row's authored id; every nav row names itself with a literal, never copy or a translated string
                      analyticsId={`${fwr.analyticsId}_dialog`}
                      href={fwr.href}
                      text={fwr.label}
                      analyticsText={fwr.analyticsText}
                      external={fwr.external}
                      suppressExternalIcon
                      className={cn(
                        "flex flex-col py-2",
                        fwr.description && "group",
                      )}
                    >
                      {fwr.description ? (
                        <>
                          <span className="flex w-fit items-center gap-2.5">
                            <span className={floatingRowTitle.primary}>
                              {fwr.label}
                            </span>
                            <RowArrow external={fwr.external} />
                          </span>
                          <span className={floatingRowDescription.primary}>
                            {fwr.description}
                          </span>
                        </>
                      ) : (
                        <span className={floatingRowTitle.primary}>
                          {fwr.label}
                        </span>
                      )}
                    </HeaderLink>
                  </li>
                );
              }
              if ("prose" in link) {
                const pl = link;
                return (
                  <li key={linkIndex}>
                    <p
                      className={cn(
                        "py-2 text-[0.875rem] leading-[1.286] md:text-[1rem]",
                        navFgMutedMobile,
                      )}
                    >
                      {pl.prose.map((seg, i) =>
                        typeof seg === "string" ? (
                          <span key={i}>{seg}</span>
                        ) : (
                          <HeaderLink
                            // eslint-disable-next-line analytics/id-must-be-static -- forwarded from the row's authored id; every nav row names itself with a literal, never copy or a translated string
                            analyticsId={`${seg.analyticsId}_dialog`}
                            key={i}
                            href={seg.href}
                            text={seg.label}
                            analyticsText={seg.analyticsText}
                            external={seg.external}
                            className={cn(
                              "inline",
                              floatingUnderlineBase,
                              floatingUnderlineSelfHover,
                              navFg,
                            )}
                          >
                            {seg.label}
                          </HeaderLink>
                        ),
                      )}
                    </p>
                  </li>
                );
              }
              const nl = link as NavLink;
              const tier = nl.tier === "primary" ? "primary" : "secondary";
              const titleClass = labeled
                ? floatingLabeledTitle
                : floatingRowTitle[tier];
              const descClass = labeled
                ? floatingLabeledDescription
                : floatingRowDescription[tier];
              const isPrimary = tier === "primary" && !labeled;
              return (
                <li key={linkIndex}>
                  <HeaderLink
                    // eslint-disable-next-line analytics/id-must-be-static -- forwarded from the row's authored id; every nav row names itself with a literal, never copy or a translated string
                    analyticsId={`${nl.analyticsId}_dialog`}
                    href={nl.href}
                    text={nl.title}
                    analyticsText={nl.analyticsText}
                    external={nl.external}
                    suppressExternalIcon
                    className={cn(
                      isPrimary ? "group" : "group/link",
                      "flex flex-col py-2",
                    )}
                  >
                    <span
                      className={cn(
                        titleClass,
                        isPrimary
                          ? "flex w-fit items-center gap-2.5"
                          : cn(
                              floatingUnderlineBase,
                              floatingUnderlineGroupHover,
                            ),
                      )}
                    >
                      {nl.title}
                      {isPrimary && (
                        <span
                          aria-hidden
                          className={cn(
                            "transition-transform duration-150 ease-out",
                            nl.external
                              ? "group-hover:-translate-y-[3px] group-hover:translate-x-[3px]"
                              : "group-hover:translate-x-[3px]",
                          )}
                        >
                          {nl.external ? "↗" : "→"}
                        </span>
                      )}
                    </span>
                    {nl.description && (
                      <span className={descClass}>{nl.description}</span>
                    )}
                  </HeaderLink>
                </li>
              );
            })}
          </ul>
        </div>
      );
    })}
  </div>
);

// Dropdown item with collapsible panel
const DropdownItem = ({
  item,
  openItems,
  onOpenItemChange,
}: {
  item: Extract<MobileMenuNavItem, { type: "dropdown" }>;
  openItems?: Set<string>;
  onOpenItemChange?: React.Dispatch<React.SetStateAction<Set<string>>>;
}) => {
  const { variant } = useHeaderContext();

  const controlledProps =
    onOpenItemChange !== undefined && openItems !== undefined
      ? {
          open: openItems.has(item.label),
          onOpenChange: (isOpen: boolean) =>
            onOpenItemChange((prev) => {
              const next = new Set(prev);
              if (isOpen) {
                next.add(item.label);
              } else {
                next.delete(item.label);
              }
              return next;
            }),
        }
      : {};

  return (
    <Collapsible.Root {...controlledProps}>
      <Collapsible.Trigger
        className={cn(
          "group flex w-full items-center justify-between",
          "px-2 py-3.5",
        )}
      >
        <Text variant="h5" className="flex w-full items-center justify-between">
          {item.label}
          <ChevronDownIcon className="group-data-panel-open:rotate-180" />
        </Text>
      </Collapsible.Trigger>

      <Collapsible.Panel className="overflow-hidden transition-all data-[ending-style]:h-0 data-[starting-style]:h-0">
        {variant === "floating" ? (
          <FloatingDropdownLinks
            sections={item.mobileLinks ?? item.links.flat()}
          />
        ) : (
          <DropdownLinks links={item.links} />
        )}
      </Collapsible.Panel>
    </Collapsible.Root>
  );
};

// Simple link item
const LinkItem = ({
  item,
  className,
}: {
  item: Extract<MobileMenuNavItem, { type: "link" }>;
  className?: string;
}) => (
  <HeaderLink
    analyticsId={item.analyticsId}
    href={item.href}
    text={item.label}
    analyticsText={item.analyticsText}
    className={cn("flex items-center px-2 py-3.5", className)}
  >
    <Text variant="h5">{item.label}</Text>
  </HeaderLink>
);

// Small link item
const SmallLinkItem = ({
  item,
  className,
}: {
  item: Extract<MobileMenuNavItem, { type: "smallLink" }>;
  className?: string;
}) => (
  <HeaderLink
    analyticsId={item.analyticsId}
    href={item.href}
    text={item.label}
    analyticsText={item.analyticsText}
    className={cn("", className)}
  >
    <Text variant="bodySm" className="inline-flex items-center gap-1">
      {item.label}
      {item.isExternal && <span aria-hidden>→</span>}
    </Text>
  </HeaderLink>
);

// Button item
const ButtonItem = ({
  item,
}: {
  item: Extract<MobileMenuNavItem, { type: "button" }>;
}) => (
  <Button
    variant={item.variant ?? "secondary"}
    size="base"
    className="w-full"
    render={
      <HeaderLink
        analyticsId={item.analyticsId}
        href={item.href}
        text={item.label}
        analyticsText={item.analyticsText}
      />
    }
  >
    {item.label}
  </Button>
);

export const DialogNavItem = ({
  item,
  className,
  openItems,
  onOpenItemChange,
}: DialogNavItemProps) => {
  switch (item.type) {
    case "dropdown":
      return (
        <DropdownItem
          item={item}
          openItems={openItems}
          onOpenItemChange={onOpenItemChange}
        />
      );
    case "link":
      return <LinkItem item={item} className={className} />;
    case "smallLink":
      return <SmallLinkItem item={item} className={className} />;
    case "button":
      return <ButtonItem item={item} />;
    case "custom":
      return <>{item.customItem}</>;
  }
};
